import { useEffect, useState } from "react";
import {
  Box,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Form, useNotify, useRefresh } from "react-admin";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import { employeeRequests } from "api/employeeApi";
import EmployeeInput from "server/components/Fields/EmployeeInput";
import { getProfessorRecordText } from "server/components/Fields/_fieldsHelper";
import { useNotifyError } from "utils/useNotifyError";
import ModalTitle, {
  withModalSx,
  modalButtonSx,
  modalCancelButtonSx,
  modalConfirmButtonSx,
  modernUiProps,
} from "server/components/Modals/ModalTitle";

const ROLE_LABELS = {
  dean: "Декан (председател)",
  head: "Ръководител на катедра",
  legal: "Член от юридическия отдел",
  substantive: "Допълнителен член (титуляр)",
  reserve: "Резервен член",
};

const EMPTY_VALUE = "—";

const PickedMemberRow = ({ label, source, employeeId, memberLabels, isReadOnly, structuralUnitId }) => (
  <Box>
    <Typography
      variant="caption"
      color="primary"
    >
      {label}
    </Typography>
    {isReadOnly ? (
      <Typography>{employeeId ? memberLabels[employeeId] || `(ID: ${employeeId})` : EMPTY_VALUE}</Typography>
    ) : (
      <EmployeeInput
        source={source}
        label="Член (от факултета)"
        fullWidth
        filters={{ structuralUnitId }}
        reference={employeeRequests.getEmployeesWithDegreeSelect()}
      />
    )}
  </Box>
);

const ExamAdmissionCommitteeDialog = ({ campaignId, seatId, structuralUnitId, mode, triggerLabel }) => {
  const notifyError = useNotifyError();
  const notify = useNotify();
  const refresh = useRefresh();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [committee, setCommittee] = useState(null);
  const [memberLabels, setMemberLabels] = useState({});

  const isReadOnly = mode === "view";
  const isCreate = mode === "create";

  const reload = async () => {
    if (isCreate) {
      setCommittee(null);
      setMemberLabels({});
      return;
    }
    setLoading(true);
    try {
      const { data: committeeData } = await dataProvider
        .customRequest(doctoralRequests.getExamAdmissionCommittee(campaignId, seatId), null, "GET")
        .catch(() => ({ data: null }));
      setCommittee(committeeData || null);

      if (committeeData) {
        const ids = [
          committeeData.deanEmployeeId,
          committeeData.departmentHeadEmployeeId,
          committeeData.legalMemberEmployeeId,
          committeeData.substantiveMemberEmployeeId,
          committeeData.reserveMemberEmployeeId,
        ].filter(Boolean);
        if (ids.length > 0) {
          const employees = await dataProvider.getMany(employeeRequests.getEmployeesWithDegreeSelect(), { ids });
          const map = {};
          (employees.data || []).forEach(e => {
            map[e.id] = getProfessorRecordText(e);
          });
          setMemberLabels(map);
        } else {
          setMemberLabels({});
        }
      } else {
        setMemberLabels({});
      }
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (!saving) setOpen(false);
  };

  const handleSave = async data => {
    if (saving) return;
    setSaving(true);
    const payload = {
      legalMemberEmployeeId: data.legalMemberEmployeeId ?? null,
      substantiveMemberEmployeeId: data.substantiveMemberEmployeeId ?? null,
      reserveMemberEmployeeId: data.reserveMemberEmployeeId ?? null,
    };
    try {
      await dataProvider.customRequest(
        isCreate
          ? doctoralRequests.createExamAdmissionCommittee(campaignId, seatId)
          : doctoralRequests.updateExamAdmissionCommittee(campaignId, seatId),
        payload,
        isCreate ? "POST" : "PUT",
      );
      notify(isCreate ? "Комисията е създадена успешно" : "Комисията е обновена успешно", { type: "success" });
      refresh();
      setOpen(false);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await dataProvider.customRequest(
        doctoralRequests.deleteExamAdmissionCommittee(campaignId, seatId),
        null,
        "DELETE",
      );
      notify("Комисията е премахната", { type: "success" });
      refresh();
      setOpen(false);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <MuiButton
        size="small"
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ whiteSpace: "nowrap", minWidth: "auto" }}
      >
        {triggerLabel}
      </MuiButton>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        sx={withModalSx()}
      >
        <ModalTitle title="Комисия по допускане до изпит" />
        {loading ? (
          <DialogContent>
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          </DialogContent>
        ) : (
          <Form
            record={committee ?? {}}
            onSubmit={handleSave}
          >
            <DialogContent sx={{ px: 4 }}>
              <Stack
                spacing={2}
                sx={{ mt: 1 }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="primary"
                  >
                    {ROLE_LABELS.dean}
                  </Typography>
                  <Typography>
                    {committee
                      ? memberLabels[committee.deanEmployeeId] || `(ID: ${committee.deanEmployeeId})`
                      : "Ще бъде попълнен автоматично при създаване."}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="primary"
                  >
                    {ROLE_LABELS.head}
                  </Typography>
                  <Typography>
                    {committee
                      ? memberLabels[committee.departmentHeadEmployeeId] ||
                        `(ID: ${committee.departmentHeadEmployeeId})`
                      : "Ще бъде попълнен автоматично при създаване."}
                  </Typography>
                </Box>
                <PickedMemberRow
                  label={ROLE_LABELS.legal}
                  source="legalMemberEmployeeId"
                  employeeId={committee?.legalMemberEmployeeId}
                  memberLabels={memberLabels}
                  isReadOnly={isReadOnly}
                  structuralUnitId={structuralUnitId}
                />
                <PickedMemberRow
                  label={ROLE_LABELS.substantive}
                  source="substantiveMemberEmployeeId"
                  employeeId={committee?.substantiveMemberEmployeeId}
                  memberLabels={memberLabels}
                  isReadOnly={isReadOnly}
                  structuralUnitId={structuralUnitId}
                />
                <PickedMemberRow
                  label={ROLE_LABELS.reserve}
                  source="reserveMemberEmployeeId"
                  employeeId={committee?.reserveMemberEmployeeId}
                  memberLabels={memberLabels}
                  isReadOnly={isReadOnly}
                  structuralUnitId={structuralUnitId}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingX: 3, paddingBottom: 2 }}>
              <Button
                size="medium"
                variant="outlined"
                {...modernUiProps({ color: "error" })}
                startIcon={<CloseIcon />}
                label={isReadOnly ? "Затвори" : "Отказ"}
                onClick={handleClose}
                disabled={saving}
                sx={modalCancelButtonSx}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                {mode === "edit" && committee && (
                  <Button
                    size="medium"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    label="Премахни"
                    onClick={handleDelete}
                    disabled={saving}
                    sx={modalButtonSx}
                  />
                )}
                {!isReadOnly && (
                  <Button
                    type="submit"
                    size="medium"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={modalConfirmButtonSx}
                    label={isCreate ? "Създай" : "Запази"}
                    disabled={saving}
                  />
                )}
              </Box>
            </DialogActions>
          </Form>
        )}
      </Dialog>
    </>
  );
};

export default ExamAdmissionCommitteeDialog;
